'use client';

import { useEffect, useState, useCallback, useSyncExternalStore } from 'react';
import { Menu } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import type { BriefingSection } from './briefing-card';
import { getBriefingViewMessages, type UiLanguage } from '@/i18n/briefing-view';

const subscribeNoop = () => () => {};

// ---------------------------------------------------------------------------
// BriefingSidebar — sticky TOC with scroll-spy (IntersectionObserver)
// ---------------------------------------------------------------------------

interface BriefingSidebarProps {
  sections: BriefingSection[];
  language?: UiLanguage;
}

export function BriefingSidebar({ sections, language = 'de' }: BriefingSidebarProps) {
  const t = getBriefingViewMessages(language);
  const [activeIndex, setActiveIndex] = useState(1);
  const [sheetOpen, setSheetOpen] = useState(false);
  // false on the server/hydration pass, true afterwards (avoids Radix useId() mismatch)
  const mounted = useSyncExternalStore(subscribeNoop, () => true, () => false);

  // Scroll-spy using IntersectionObserver
  useEffect(() => {
    const elements = sections
      .map((s) => document.getElementById(`section-${s.index}`))
      .filter(Boolean) as HTMLElement[];

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Find the topmost visible entry
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort(
            (a, b) =>
              a.boundingClientRect.top - b.boundingClientRect.top
          );

        if (visible.length > 0) {
          const id = visible[0].target.id;
          const match = id.match(/section-(\d+)/);
          if (match) {
            setActiveIndex(Number(match[1]));
          }
        }
      },
      {
        rootMargin: '-80px 0px -60% 0px',
        threshold: 0,
      }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [sections]);

  const handleClick = useCallback(
    (index: number) => {
      const el = document.getElementById(`section-${index}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      setSheetOpen(false);
    },
    []
  );

  const tocContent = (
    <div>
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
        {t.toc}
      </h3>
      <ul className="space-y-0.5">
        {sections.map((section) => (
          <li key={section.index}>
            <button
              onClick={() => handleClick(section.index)}
              className={cn(
                'flex items-center gap-2 rounded-md px-2 py-2.5 sm:py-1.5 text-xs font-medium transition-colors w-full text-left min-h-[44px] sm:min-h-0',
                activeIndex === section.index
                  ? 'bg-accent/10 text-accent'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              )}
            >
              <span
                className={cn(
                  'flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold flex-shrink-0',
                  activeIndex === section.index
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                {section.label ?? section.index}
              </span>
              <span className="truncate">{section.title}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar — sticky */}
      <nav className="hidden lg:block w-56 flex-shrink-0 print:hidden overflow-y-auto">
        {tocContent}
      </nav>

      {/* Mobile/Tablet — Sheet-based sidebar, rendered after hydration
          to avoid Radix useId() SSR/CSR mismatch on aria-controls */}
      {mounted && (
        <div className="lg:hidden print:hidden fixed bottom-4 right-4 z-40">
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button
                size="icon"
                className="h-11 w-11 rounded-full shadow-lg"
              >
                <Menu className="h-4 w-4" />
                <span className="sr-only">{t.tocButton}</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-6">
              <SheetHeader>
                <SheetTitle className="text-sm">{t.navigation}</SheetTitle>
              </SheetHeader>
              <div className="mt-4">
                {tocContent}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      )}
    </>
  );
}
